import { GridApi, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { MutableRefObject, useCallback, useMemo, useState } from 'react';
import { ZodArray, ZodType } from 'zod';

import { useGridValidator, useValidationTrigger } from './useLineItemsGridHandlers.helper';

interface UseLineItemsGridHandlersProps<T extends Record<string, any>> {
  apiRef: MutableRefObject<GridApi>;
  schema: ZodArray<ZodType<T>>;
  rowBuilder: (data: { length: number }) => { id: string } & unknown;
}

export const useLineItemsGridHandlers = <T extends Record<string, any>>({
  apiRef,
  schema,
  rowBuilder,
}: UseLineItemsGridHandlersProps<T>) => {
  const { triggeredValidations, shouldValidate } = useValidationTrigger();

  const { getRawRow, getRawRows } = useGridValidator({
    apiRef,
    schema,
  });

  const handleAddRow = useCallback(() => {
    const items = getRawRows();
    const newLineItem = rowBuilder({ length: items.length });

    apiRef.current.updateRows([{ ...newLineItem, action: 'insert' }]);
    apiRef.current.selectRow(newLineItem.id, true, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowBuilder]);

  const handleRowDelete = useCallback((id: any) => {
    apiRef.current.updateRows([{ id, _action: 'delete' }]);
    apiRef.current.selectRows([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseDrawer = useCallback(() => {
    if (apiRef.current && apiRef.current.getSelectedRows) {
      const ids = Array.from(apiRef.current.getSelectedRows().keys());
      apiRef.current.selectRows(ids, false);
      shouldValidate(ids[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const currentSelectedRowId = useMemo(() => rowSelectionModel[0], [rowSelectionModel]);

  const handleLineItemChange = useCallback(
    (lineItem: any) => {
      const id = rowSelectionModel[0];

      // Enforce validation only, if previous action wasn't the initial insert (handleAddRow)
      // Validation is enforced on closing the drawer latest (handleCloseDrawer)
      // if any update takes place (action is update), we want to validate the row
      const lastGridRowAction = apiRef?.current.getRowModels().get(id)?.action;
      if (lastGridRowAction !== 'insert') {
        shouldValidate(id);
      }
      apiRef?.current.updateRows([{ ...lineItem, id, action: 'update' }]);
    },
    [apiRef, rowSelectionModel, shouldValidate],
  );

  return {
    currentSelectedRowId,
    validate: triggeredValidations[currentSelectedRowId],
    selectedLineItem: getRawRow(currentSelectedRowId),
    setRowSelectionModel,
    handleAddRow,
    handleRowDelete,
    handleCloseDrawer,
    handleLineItemChange,
  };
};
